import { ErrorTypes, createErrorWithType } from '../../utils/errorHandling';

/**
 * AuthService - Centralized service for handling authentication
 * Responsible for token validation, login, and authentication state
 */
class AuthService {
  constructor() {
    this.token = localStorage.getItem('auth_token');
    this.user = JSON.parse(localStorage.getItem('auth_user') || 'null');
  }

  /**
   * Check if user is authenticated
   * @returns {boolean} True if user has a valid token
   */
  isAuthenticated() {
    return !!this.token;
  }

  /**
   * Get the current authentication token
   * @returns {string|null} The current token or null if not authenticated
   */
  getToken() {
    return this.token;
  }

  /**
   * Get the current authenticated user
   * @returns {Object|null} The current user or null if not authenticated
   */
  getUser() {
    return this.user;
  }

  /**
   * Authenticate with a token
   * @param {string} token - The authentication token
   * @returns {Promise<Object>} The authenticated user
   * @throws {Error} If token validation fails
   */
  async login(token) {
    try {
      if (!token || typeof token !== 'string' || token.trim() === '') {
        throw createErrorWithType(
          ErrorTypes.AUTH,
          'Token inválido. Por favor, introduce un token válido.'
        );
      }

      // Validate token with the server
      const response = await fetch('/api/token/validate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ token })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw createErrorWithType(
          ErrorTypes.AUTH,
          errorData.message || 'Error al validar el token. Por favor, intenta de nuevo.'
        );
      }

      const data = await response.json();
      
      if (!data.success) {
        throw createErrorWithType(
          ErrorTypes.AUTH,
          data.message || 'Token inválido. Por favor, introduce un token válido.'
        );
      }

      // Store token and user data
      this.token = token;
      this.user = data.user || { token };
      
      localStorage.setItem('auth_token', token);
      localStorage.setItem('auth_user', JSON.stringify(this.user));
      
      return this.user;
    } catch (error) {
      // Re-throw authentication errors
      if (error.type === ErrorTypes.AUTH) {
        throw error;
      }
      
      // Convert other errors to authentication errors
      throw createErrorWithType(
        ErrorTypes.AUTH,
        error.message || 'Error al iniciar sesión. Por favor, intenta de nuevo.'
      );
    }
  }

  /**
   * Log out the current user
   * @returns {Promise<void>}
   */
  async logout() {
    try {
      // Call logout API if authenticated
      if (this.token) {
        await fetch('/api/logout', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.token}`
          }
        });
      }
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      // Clear local storage and state regardless of API success
      this.token = null;
      this.user = null;
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
    }
  }

  /**
   * Check if a token is valid
   * @param {string} token - The token to validate
   * @returns {Promise<boolean>} True if token is valid
   */
  async validateToken(token) {
    try {
      const response = await fetch('/api/token/validate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ token })
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      return data.success === true;
    } catch (error) {
      console.error('Error validating token:', error);
      return false;
    }
  }
}

// Export a singleton instance
export default new AuthService();